import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// A WWI field gun: a gunmetal barrel on a recoil cradle behind an armour
// shield, carried on two large spoked wheels with split trail legs to the
// rear. The barrel points +Z, elevated about 20 degrees, and is named so the
// one-shot "fire" animation can recoil it back along its own axis and return.
const GUNMETAL = "#4a4d52";
const OLIVE = "#5f6b3c";
const TIMBER = "#8b6438";
const BRASS = "#b08d3a";

const ELEV = 0.35; // barrel elevation in radians (~20 degrees)
const DIR: readonly [number, number, number] = [0, Math.sin(ELEV), Math.cos(ELEV)];
const WHEEL_R = 0.19;
const AXLE_Y = WHEEL_R;
const BREECH: readonly [number, number, number] = [0, 0.24, -0.14];

// One spoked wooden wheel centred on the axle at x. Rim + hub + three spokes.
function wheel(x: number): Part[] {
  const out: Part[] = [
    { geometry: new THREE.CylinderGeometry(WHEEL_R, WHEEL_R, 0.045, 12),
      color: TIMBER, position: [x, AXLE_Y, 0], rotation: [0, 0, Math.PI / 2] },
    { geometry: new THREE.CylinderGeometry(0.045, 0.045, 0.07, 8),
      color: GUNMETAL, position: [x, AXLE_Y, 0], rotation: [0, 0, Math.PI / 2] },
  ];
  for (let i = 0; i < 3; i++) {
    const ang = (i * Math.PI) / 3;
    out.push({
      geometry: new THREE.BoxGeometry(0.02, 0.34, 0.035), color: TIMBER,
      position: [x + (i === 0 ? 0.028 : -0.028), AXLE_Y, 0], rotation: [ang, 0, 0],
    });
  }
  return out;
}

// One split trail leg reaching back from under the cradle, plus its spade.
function trail(side: 1 | -1): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.05, 0.05, 0.42), color: OLIVE,
      position: [side * 0.1, 0.1, -0.22], rotation: [-0.18, -side * 0.28, 0] },
    { geometry: new THREE.BoxGeometry(0.07, 0.06, 0.05), color: GUNMETAL,
      position: [side * 0.16, 0.03, -0.41] },
  ];
}

export const fieldHowitzer = defineModel({
  id: "field-howitzer",
  tiles: { w: 1, h: 1 },
  batchId: "wwi",
  author: "Fable 5",
  name: "Field Howitzer",
  tags: ["prop", "wwi", "military", "artillery", "weapon", "animated"],
  summary: "A WWI field gun with shield, spoked wheels and a recoiling barrel.",
  footprint: 0.42,
  parts(): Part[] {
    return [
      ...wheel(-0.26),
      ...wheel(0.26),
      // Axle between the wheels.
      { geometry: new THREE.CylinderGeometry(0.025, 0.025, 0.52, 8),
        color: GUNMETAL, position: [0, AXLE_Y, 0], rotation: [0, 0, Math.PI / 2] },
      // Recoil cradle the barrel rides on, tilted with the elevation.
      { geometry: new THREE.BoxGeometry(0.1, 0.08, 0.3), color: OLIVE,
        position: [0, 0.21, -0.02], rotation: [ELEV, 0, 0] },
      // Armour shield plate, leaning slightly back between the wheels.
      { geometry: new THREE.BoxGeometry(0.52, 0.34, 0.02), color: OLIVE,
        position: [0, 0.32, 0.08], rotation: [-0.14, 0, 0] },
      // The gun itself: recoil cylinder, tapered barrel, breech block, muzzle
      // band — all named "barrel" so the fire recoil moves them as one.
      { geometry: new THREE.CylinderGeometry(0.035, 0.035, 0.22, 8), color: GUNMETAL, name: "barrel",
        position: [0, 0.245, -0.06], rotation: [Math.PI / 2 - ELEV, 0, 0] },
      { geometry: new THREE.CylinderGeometry(0.028, 0.045, 0.6, 10), color: GUNMETAL, name: "barrel",
        position: [0, 0.33, 0.13], rotation: [Math.PI / 2 - ELEV, 0, 0] },
      { geometry: new THREE.BoxGeometry(0.1, 0.1, 0.1), color: GUNMETAL, name: "barrel",
        position: [BREECH[0], BREECH[1], BREECH[2]], rotation: [ELEV, 0, 0] },
      { geometry: new THREE.CylinderGeometry(0.036, 0.036, 0.04, 10), color: BRASS, name: "barrel",
        position: [0, 0.42, 0.38], rotation: [Math.PI / 2 - ELEV, 0, 0] },
      ...trail(1),
      ...trail(-1),
    ];
  },
  animations: [
    {
      id: "fire",
      label: "Fire",
      duration: 0.7,
      loop: false,
      tracks: [
        {
          target: "barrel",
          pivot: [BREECH[0], BREECH[1], BREECH[2]],
          position: [
            { t: 0, value: [0, 0, 0] },
            // Quick recoil back along the barrel axis...
            { t: 0.08, value: [0, -0.13 * DIR[1], -0.13 * DIR[2]], ease: "easeOut" },
            // ...then run smoothly back into battery.
            { t: 0.7, value: [0, 0, 0], ease: "easeInOut" },
          ],
        },
      ],
    },
  ],
});
